import { useCallback, useEffect, useState } from "react";
import { supabase } from "../lib/supabaseClient";
import type { Database } from "@wheels-crm/shared-types";

type OrderRow = Database["public"]["Tables"]["orders"]["Row"];
type ClientRow = Database["public"]["Tables"]["clients"]["Row"];
type ReminderRow = Database["public"]["Tables"]["reminders"]["Row"];

export type OrderWithRelations = OrderRow & {
  client: ClientRow;
  reminders: ReminderRow[];
};

const SELECT = "*, client:clients(*), reminders(*)";

/**
 * Loads all orders (newest first) with their client and reminders embedded, and keeps the
 * list live via Supabase Realtime — per AGENTS.md §3, the SPA talks to Postgres directly.
 */
export function useOrders() {
  const [orders, setOrders] = useState<OrderWithRelations[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    const { data, error: fetchError } = await supabase
      .from("orders")
      .select(SELECT)
      .order("created_at", { ascending: false });

    if (fetchError) {
      setError(fetchError.message);
    } else {
      setError(null);
      setOrders((data ?? []) as unknown as OrderWithRelations[]);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    refetch();

    const channel = supabase
      .channel("orders-realtime")
      .on("postgres_changes", { event: "*", schema: "public", table: "orders" }, () => refetch())
      .on("postgres_changes", { event: "*", schema: "public", table: "reminders" }, () => refetch())
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [refetch]);

  return { orders, loading, error, refetch };
}
